/* PODbySoni UX layer. Added Aug 8, 2026, from Ujjval's fourth round.
   Loaded from sections/pbs-marquee.liquid, which is in header-group and
   therefore renders on every page. Loads AFTER podbysoni-brand.js and
   podbysoni-header.js, and depends on neither.

   Contents
     1. Reveal on scroll
     2. Shop menu opens on hover
     3. Product gallery, colour swap made instant
     4. "You may also like" cards get wired up when they arrive
     5. Sale window guard (shop.metafields.pbs.sale), its own block at the end

   THE STANDING WARNING IN brand.js AND header.js APPLIES HERE TOO. Do not add a
   page-wide MutationObserver. Those files write to the DOM, a page-wide observer
   sees its own writes, re-triggers itself and locks the tab up hard. The two
   observers below are both scoped to a single element and both stop watching as
   soon as they have what they came for.

   ONE MORE THING WORTH KNOWING BEFORE YOU DEBUG ANYTHING IN HERE. In a
   BACKGROUND tab Chrome suspends IntersectionObserver callbacks and freezes CSS
   transitions. Everything in this file that depends on either will look broken
   if you inspect it from a tab that is not in front: sections stay at opacity 0,
   and Dawn's product recommendations never fetch. That is not a bug, and the
   safety net in reveals() exists partly because of it. Check with the tab
   focused before changing anything. */
(function () {
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function idle(fn) {
    if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 400);
  }

  /* ======================================================================
     1. REVEAL ON SCROLL
     Ujjval, round 4: the prototype rises each section into place on the way
     down the product page; Shopify just had them sitting there.

     WHY THE CLASS IS ADDED HERE AND NOT IN THE MARKUP
     .pbs-rv starts at opacity 0. If Liquid printed it, then a JavaScript error,
     a blocked script or an old browser would leave the whole lower half of
     every page invisible with nothing to bring it back. Adding it from script
     means the failure mode is "no animation", which nobody notices.

     WHY ONLY BELOW THE FOLD
     Hiding something already on screen and fading it back in is a flash, not a
     reveal. Anything within the first screen and a bit is left alone.
     ====================================================================== */
  var REVEAL = [
    '.pbs-hl',                    /* product page, Why you'll love it */
    '.pbs-rev-wrap',              /* product page, What the flock says */
    '.related-products',          /* product page, You may also like */
    '.pbs-prod-wrap',             /* home, Best sellers */
    '.pbs-fyp__inner',            /* home, Who are you shopping for */
    '.pbs-story__head',           /* home, How it is made heading */
    '.pbs-rvc-wrap',              /* home, review carousel */
    '.pbs-flock-wrap',            /* Join the flock, on every template */
    '.pbs-collection-foot',
    '.pbs-about__passage',
    '.pbs-contact__cards',
    '.pbs-colhero-wrap',          /* collection hero (step 09 REQUEST) */
    '.pbs-colbar-wrap',           /* collection toolbar (step 09 REQUEST) */
    '#product-grid .grid__item',  /* collection grid cards (step 09 REQUEST) */
    '[data-pbs-reveal]'           /* opt-in hook for rebuilt sections; value 1-4 = stagger step */
  ].join(',');

  function reveals() {
    if (REDUCED || !('IntersectionObserver' in window) || window.matchMedia('(max-width: 749px)').matches) return;

    var els = [].slice.call(document.querySelectorAll(REVEAL)).filter(function (el) {
      if (el.dataset.pbsRv) return false;
      /* 0.9 of the viewport rather than 1.0: an element whose top is a sliver
         below the fold is close enough that the user sees it move as soon as
         they touch the wheel, which reads as responsive rather than delayed. */
      return el.getBoundingClientRect().top > window.innerHeight * 0.9;
    });

    if (!els.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -8% 0px' });

    els.forEach(function (el) {
      el.dataset.pbsRv = '1';
      el.classList.add('pbs-rv');
      var step = parseInt(el.getAttribute('data-pbs-reveal'), 10);
      if (step >= 1 && step <= 4) el.classList.add('pbs-rv-d' + step);
      io.observe(el);
    });

    /* THE SAFETY NET, and it is not paranoia. This class starts at opacity 0,
       so the cost of the observer not firing is an INVISIBLE SECTION, not a
       missing animation. Seen for real while testing this on Aug 8: in a
       background tab the Best sellers block sat completely blank, because
       Chrome suspends both the observer and the transition until the tab is in
       front. A shopper is looking at their tab so it would not bite them, but
       it is exactly the shape of failure to insure against.

       Five sweeps, two seconds apart, revealing anything that is already inside
       the viewport and still hidden. Sweeps while the tab is hidden do not
       count, so a page opened in a background tab still gets its five once it
       comes forward, and the hard stop at 30 means the timer can never outlive
       a minute of it never coming forward. */
    var sweeps = 0;
    var net = setInterval(function () {
      sweeps++;
      if (sweeps >= 30) { clearInterval(net); return; }
      if (document.hidden) return;
      els.forEach(function (el) {
        if (el.classList.contains('is-in')) return;
        if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-in');
      });
      if (sweeps >= 5) clearInterval(net);
    }, 2000);
  }

  /* ======================================================================
     2. SHOP MENU ON HOVER
     Ujjval, round 4 item 5: the dropdown should open on hover, it currently
     only opens on click.

     Dawn's mega menu is a real <details> element driven by <header-menu> in
     global.js. CSS cannot open a <details>, so hover has to set the open
     attribute. Everything Dawn already does keeps working: click still
     toggles, Escape still closes, keyboard focus still opens it, and a touch
     device, which has no hover at all, behaves exactly as it did before.

     THE GRACE PERIOD IS THE WHOLE TRICK. Closing the instant the pointer
     leaves makes the menu feel like it is running away, because the path from
     the word "Shop" down into the panel crosses a few pixels of header. 180ms
     is long enough to cross it and short enough that nobody notices a menu
     lingering. The CSS adds a bridge under the summary as well, so in practice
     the timer rarely has to save it.
     ====================================================================== */
  function hoverMenus() {
    var items = document.querySelectorAll('.header__inline-menu details.mega-menu, .header__inline-menu header-menu > details');
    if (!items.length) return;

    /* A pointer that cannot hover (a finger) must not open menus on the way
       past. matchMedia is the reliable test; pointerType on the event is not,
       because some trackpads report "pen". */
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    items.forEach(function (d) {
      if (d.dataset.pbsHover) return;
      d.dataset.pbsHover = '1';

      var t = null;

      function open() {
        clearTimeout(t);
        if (d.open) return;
        /* Close any sibling that is open, so moving along the nav swaps panels
           instead of stacking them. */
        items.forEach(function (o) { if (o !== d) o.open = false; });
        d.open = true;
      }

      function close() {
        clearTimeout(t);
        t = setTimeout(function () {
          /* Do not steal the menu from someone tabbing through it. */
          if (d.contains(document.activeElement)) return;
          d.open = false;
        }, 180);
      }

      d.addEventListener('pointerenter', open);
      d.addEventListener('pointerleave', close);

      var panel = d.querySelector('.mega-menu__content, .header__submenu');
      if (panel) {
        panel.addEventListener('pointerenter', open);
        panel.addEventListener('pointerleave', close);
      }
    });
  }

  /* ======================================================================
     3. PRODUCT GALLERY, COLOUR SWAP
     Ujjval, round 4 item 1: "significant delay when switching between colors."

     WHAT IS ACTUALLY SLOW. Nothing in the matching code. sections/pbs-product
     holds ONE hero image and rewrites its src, which is the right design; the
     first build stacked all 20 images and took seconds to open. But a src
     rewrite to a URL the browser has never seen is a network fetch. Measured on
     the draft on Aug 8: 74 KB and 419 ms per 1200px hero. The old picture stays
     on screen for that whole time, which is exactly what "delay" means here.

     THE FIX IS CACHE, NOT CODE. Three passes, cheapest first:

       a. On hover or keyboard focus of a swatch or a thumbnail, start fetching
          that picture. A pointer resting on a swatch is a couple of hundred
          milliseconds of warning, which covers half the round trip before the
          click even happens.
       b. At idle after load, fetch the hero of every COLOUR. That is one image
          per swatch, five on most of these products, not all twenty.
       c. At idle after that, and only on a connection that can afford it,
          fetch the rest of the strip so the arrows are instant too.

     Images are held in a module-level array as well as being requested. That is
     not decoration: an Image() with no reference can be garbage collected, and
     on some builds that evicts it from the memory cache too.

     This never touches sections/pbs-product.liquid. It only warms the cache the
     existing code reads from, so if this file is ever removed the page still
     works, just slower.
     ====================================================================== */
  var warmed = Object.create(null);
  var held = [];

  function warm(src) {
    if (!src || warmed[src]) return;
    warmed[src] = true;
    var im = new Image();
    im.decoding = 'async';
    im.src = src;
    held.push(im);
    /* 60 is well past the largest product in the catalogue. The cap exists so a
       future product with a hundred mockups cannot pin megabytes in memory. */
    if (held.length > 60) held.shift();
  }

  function cheapConnection() {
    var c = navigator.connection;
    if (!c) return false;
    if (c.saveData) return true;
    return /2g/.test(c.effectiveType || '');
  }

  function gallery() {
    var wrap = document.querySelector('.pbs-pdp-wrap');
    if (!wrap || wrap.dataset.pbsUx) return;
    wrap.dataset.pbsUx = '1';

    var thumbs = [].slice.call(wrap.querySelectorAll('[data-pbs-thumb]'));
    if (!thumbs.length) return;

    var byMedia = {};
    thumbs.forEach(function (b) { byMedia[b.dataset.pbsThumb] = b.dataset.pbsSrc; });

    /* A swatch does not carry its own picture, so map through the variant table
       the section already prints: colour, then variant, then media id, then the
       large src that rides on that media's thumbnail. */
    var variants = [];
    try {
      variants = JSON.parse(document.querySelector('[data-pbs-variants]').textContent);
    } catch (e) { /* the section prints this; if it is missing, only (a) works */ }

    /* Work out WHICH option position holds the colour before matching anything.
       Option order is not the same on every product here: the L&D sweatshirt is
       Size / Color and the NICU tee is Color / Size. The section's own comment
       says the same thing for the same reason. Matching on "is this name\n       anywhere in the options array" would also work today, but it would start
       warming the wrong picture the day a colour and a size share a name. */
    var swatchBtns = [].slice.call(wrap.querySelectorAll('[data-pbs-color]'));
    var colorIndex = -1;

    if (swatchBtns.length && variants.length) {
      var probe = swatchBtns[0].getAttribute('data-pbs-color');
      for (var v = 0; v < variants.length && colorIndex < 0; v++) {
        colorIndex = variants[v].opts.indexOf(probe);
      }
    }

    var colorFirst = {};

    swatchBtns.forEach(function (btn) {
      var name = btn.getAttribute('data-pbs-color');

      if (colorIndex > -1) {
        for (var i = 0; i < variants.length; i++) {
          if (variants[i].opts[colorIndex] !== name || !variants[i].media) continue;
          var src = byMedia[String(variants[i].media)];
          if (src) { colorFirst[name] = src; break; }
        }
      }

      var hint = function () { warm(colorFirst[name]); };
      btn.addEventListener('pointerenter', hint);
      btn.addEventListener('focus', hint);
    });

    thumbs.forEach(function (b) {
      var hint = function () { warm(b.dataset.pbsSrc); };
      b.addEventListener('pointerenter', hint);
      b.addEventListener('focus', hint);
    });

    idle(function () {
      Object.keys(colorFirst).forEach(function (k) { warm(colorFirst[k]); });
      if (cheapConnection()) return;
      idle(function () {
        thumbs.forEach(function (b) { warm(b.dataset.pbsSrc); });
      });
    });

    /* Feedback for the one case preloading cannot cover: a click on a colour
       whose photo genuinely is not cached yet. Dip the image while it loads so
       the click is acknowledged instead of appearing to do nothing.

       THIS IS THE SCOPED OBSERVER THE HEADER NOTE MENTIONS. It watches ONE
       element for ONE attribute, src. Adding a class is not an attribute this
       observer subscribes to, so it cannot see its own effect and cannot loop.
       Do not widen it to attributes: true. */
    var hero = wrap.querySelector('[data-pbs-heroimg]');
    var box = wrap.querySelector('[data-pbs-hero]');
    if (!hero || !box || !('MutationObserver' in window)) return;

    function settle() { box.classList.remove('is-swapping'); }

    hero.addEventListener('load', settle);
    hero.addEventListener('error', settle);

    new MutationObserver(function () {
      if (hero.complete && hero.naturalWidth) { settle(); return; }
      box.classList.add('is-swapping');
    }).observe(hero, { attributes: true, attributeFilter: ['src'] });
  }

  /* ======================================================================
     4. "YOU MAY ALSO LIKE"
     Ujjval, round 4 item 4: the colours there do nothing on click, and should
     also work on hover.

     THE CAUSE. The swatches on a product card are wired by initCards() in
     podbysoni-brand.js, which runs on DOMContentLoaded and on
     shopify:section:load. Dawn's product-recommendations element fetches its
     cards over the network the first time it scrolls near the viewport and
     injects them, and it fires NEITHER of those events. So on the homepage and
     the collection page the swatches work, and in this one strip they are inert
     markup. Verified on the draft on Aug 8: the fetched HTML contains 4 cards
     and 22 swatches, all of them unwired.

     THE FIX. Watch that ONE element until it has cards in it, hand them to
     initCards, and stop watching. brand.js guards each card with
     data-pbs-ready, so calling it again is free.

     A poll would also work and was written first. The observer is better here
     because the fetch time depends on how fast the shopper scrolls, so any
     fixed delay is either too early or wasteful.
     ====================================================================== */
  function recommendations() {
    var el = document.querySelector('product-recommendations, .related-products');
    if (!el || el.dataset.pbsRec) return;
    if (!('MutationObserver' in window)) return;
    el.dataset.pbsRec = '1';

    /* STOP WATCHING BEFORE WIRING, NOT AFTER. This is the loop the warning at
       the top of the file is about, in miniature. Re-firing the event makes
       brand.js write data-pbs-ready attributes and repaint every heart, which
       are mutations inside the element this observer is watching. Disconnecting
       first means those writes are never seen. MutationObserver callbacks are
       async, so disconnecting on the line after would also have worked, but
       only by accident, and the next person to reorder these two lines would
       have no way of knowing that. */
    function wire(mo) {
      if (!el.querySelector('[data-pbs-card]')) return false;
      if (mo) mo.disconnect();

      /* brand.js exposes no function to call, so re-fire the event it already
         listens for. Its handler re-inits from e.target, which for an event
         dispatched on document is the whole page; every card it has already
         done is guarded by data-pbs-ready, so that is cheap. */
      document.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { pbs: true } }));
      reveals();
      return true;
    }

    if (wire(null)) return;

    var mo = new MutationObserver(function () { wire(mo); });
    mo.observe(el, { childList: true, subtree: true });

    /* Never leave an observer running on a page nobody is going to scroll. */
    setTimeout(function () { mo.disconnect(); }, 30000);
  }

  /* ---------- wiring ---------- */
  function init() {
    reveals();
    hoverMenus();
    gallery();
    recommendations();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    hoverMenus();
    gallery();
  });

  /* A page opened in a background tab gets its observers suspended. Sweep again
     the moment it comes forward. */
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) return;
    document.querySelectorAll('.pbs-rv').forEach(function (el) {
      if (el.classList.contains('is-in')) return;
      if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-in');
    });
    reveals();
  });

  window.addEventListener('resize', function () {
    clearTimeout(window.__pbsRvT);
    window.__pbsRvT = setTimeout(reveals, 300);
  });
})();

/* 5. SALE WINDOW GUARD, Sep 21 2026.
   While the store-wide sale in shop.metafields.pbs.sale is live, Liquid renders
   sale markup marked [data-pbs-sale] and the normal price beside it marked
   [data-pbs-sale-off] + hidden, both carrying data-pbs-sale-starts / -ends (see
   snippets/pbs-sale-state.liquid). Shopify caches pages, so a page rendered
   during the sale can be served after it ends. This re-checks both ends of the
   window against the browser clock, swaps back to the normal price outside it,
   and re-runs at the next boundary if that is within a day. No observer:
   everything it touches is in the server-rendered HTML. pbsSaleLive is used by
   the product page and pbs-quick-add.js to pick the Add to bag price. */
(function () {
  function live(el) {
    var now = Date.now();
    var s = Date.parse(el.getAttribute('data-pbs-sale-starts'));
    var e = Date.parse(el.getAttribute('data-pbs-sale-ends'));
    if (!isNaN(s) && now < s) return false;
    if (!isNaN(e) && now > e) return false;
    return true;
  }
  window.pbsSaleLive = live;

  function guard() {
    var next = Infinity;
    var now = Date.now();
    document.querySelectorAll('[data-pbs-sale], [data-pbs-sale-off]').forEach(function (el) {
      var on = live(el);
      el.hidden = el.hasAttribute('data-pbs-sale') ? !on : on;
      ['data-pbs-sale-starts', 'data-pbs-sale-ends'].forEach(function (a) {
        var t = Date.parse(el.getAttribute(a));
        if (a === 'data-pbs-sale-ends') t += 1000;
        if (!isNaN(t) && t > now && t < next) next = t;
      });
    });
    if (next - now < 864e5) setTimeout(guard, next - now + 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', guard);
  } else {
    guard();
  }
})();
